import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import { provideAnimations } from '@angular/platform-browser/animations'
import { providers as cookieProviders } from '@features/cookie-consents/providers'
import { providers as authProviders } from '@features/auth/providers'
import { interceptors } from '@features/http/interceptors'
import { providers as sentryProviders } from './sentry'
import { provideScriptSources } from '@features/script-loader'
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar'
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog'
import { providers as i18nProviders, USER_LOCALE } from '@features/i18n/providers'
import {
  SF_DEFAULT_IFRAME_BODY_CLASS_LIST,
  SF_DEFAULT_TEMPLATE_LOCALE,
  SF_TEMPLATE_LINK_DIALOG_CONFIG,
  TemplateLinkDialogConfig,
} from '@sun-finance/templating'
import { providePrefixedTitleStrategy, ScriptLoader, SF_API_PREFIX_INTERCEPTOR_BASE_URL, SF_COPY_DEFAULT_COLOR } from '@sun-finance/utils'
import {
  DeviceDataCollectorService,
  ExternalProvidersDeviceDataCollectorService,
  NoopDeviceDataCollectorService,
} from '@features/risks-integrations/device-data-collector.service'
import { SETTINGS_REGISTRY_FACTORY } from '@features/settings/settings-registry'
import { ICONS_FACTORY } from './icons'
import { COMPANY_ID } from '@features/client/client.service'

import { WINDOW, windowFactory } from '@features/storage/window'
import { environment } from '@env'
import { ENV } from './env-registry'
import { ApplicationConfig, DOCUMENT, importProvidersFrom, isDevMode, Provider } from '@angular/core'
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router'
import { routes } from './app.routes'
import { DOMAIN_NAME } from '@app/tokens'
import { RECAPTCHA_SITE_KEY } from "@features/recaptcha/recaptcha.config"
import { MAT_CARD_CONFIG } from "@angular/material/card"

const materialProviders: Provider[] = [
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: { ...(new MatDialogConfig()), panelClass: ['no-scroll-dialog', 'mobile-full-screen-dialog'], autoFocus: 'dialog', maxWidth: '100%' },
  },
  { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { verticalPosition: 'top', duration: 3000 } },
  { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
]

const sfLibsProviders: Provider[] = [
  { provide: SF_DEFAULT_TEMPLATE_LOCALE, useExisting: USER_LOCALE },
  { provide: SF_DEFAULT_IFRAME_BODY_CLASS_LIST, useValue: ['layout-container'] },
  { provide: SF_COPY_DEFAULT_COLOR, useValue: 'primary' },
  {
    provide: SF_TEMPLATE_LINK_DIALOG_CONFIG,
    useValue: {
      ...(new MatDialogConfig()),
      defaults: {},
      minWidth: '80vw',
      maxWidth: '100%',
      minHeight: '76vh',
      panelClass: ['no-scroll-dialog', 'mobile-full-screen-dialog'],
      restoreFocus: false,
    } as TemplateLinkDialogConfig,
  },
]

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      // withDebugTracing(),
    ),
    importProvidersFrom(MatSnackBarModule),
    {
      provide: WINDOW,
      useFactory: windowFactory,
      deps: [DOCUMENT],
    },
    {
      provide: ENV,
      useValue: environment,
    },
    {
      provide: COMPANY_ID,
      useValue: environment.companyId,
    },
    {
      provide: RECAPTCHA_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    ICONS_FACTORY,
    SETTINGS_REGISTRY_FACTORY,
    {
      provide: DeviceDataCollectorService,
      useFactory: (loader: ScriptLoader, window: Window) => isDevMode()
        ? new NoopDeviceDataCollectorService()
        : new ExternalProvidersDeviceDataCollectorService(loader, window),
      deps: [ScriptLoader, WINDOW],
    },
    {
      provide: DOMAIN_NAME,
      useFactory: (window: Window | null) => window ? window.location.hostname.replace('www.', '') : '',
      deps: [WINDOW]
    },
    ...materialProviders,
    ...sfLibsProviders,
    providePrefixedTitleStrategy(environment.appName),
    provideScriptSources(),
    ...sentryProviders,
    ...i18nProviders,
    ...authProviders,
    ...cookieProviders,
    provideAnimations(),
    { provide: SF_API_PREFIX_INTERCEPTOR_BASE_URL, useValue: environment.apiUrl },
    provideHttpClient(withInterceptors(interceptors), withFetch()),
  ],
}
